/**
 * Transactional edits to a Distro document.
 *
 * A batch of ops is applied to an isolated candidate document; the FINAL candidate
 * is validated; if valid, the ops replay onto the live CRDT in one changeset —
 * otherwise nothing changes and precise diagnostics return. The resulting document
 * must be valid regardless of the sequence used to produce it, so a transaction may
 * pass through invalid intermediate states (change a kind, drop the old branch's
 * props, add the new branch's) that per-operation validation would reject.
 *
 * The contract a committed transaction guarantees is "structurally valid with
 * semantic diagnostics":
 * - BLOCKS: new schema violations relative to the starting document (pre-existing
 *   debt is tolerated, including when its position shifts), new blank ids, increased
 *   duplicate-id multiplicity, and regressions of references that used to resolve
 *   (definition references correlated by their own id, navigation targets by id
 *   membership).
 * - WARNS: newly-introduced references that don't resolve yet (the definition may
 *   arrive in the next transaction; nothing else in the system checks this at all).
 * - DOES NOT CHECK: full Distro domain validity (validateDistro) — that's the
 *   caller's `checkConfigValidity` surface, or the injected `validateCandidate`.
 *
 * HEADLESS CONSTRAINT: this module must stay importable outside a browser — it may
 * only depend on ./nodes and ./collab (no React, no DOM). A server-side agent
 * harness reconstructs a TrackedObject from stored collab ops, runs a transaction,
 * and persists the emitted ChangeSet; the browser editor is just one client.
 *
 * Used by the Distro AI chat's editConfig tool. Paths use the same dot/bracket
 * notation the AI already sees from searchConfig / getObject
 * (e.g. "survey[0].components[1]").
 */
import { checkRawMatches, deref, stringConstraints, Node, Schema } from './nodes';
import {
  closeChangeset,
  createFromRawValue,
  openChangeset,
  SortFunction,
  State,
  TrackedArray,
  TrackedObject,
  TrackedRecord,
} from './collab';

// ---------------------------------------------------------------------------
// Public types — shared by every interface (browser tool, future MCP/server).
// ---------------------------------------------------------------------------

export type ConfigEditOp =
  /** Overwrite the value at `path`. If the last segment is a not-yet-set object property, adds it. */
  | { op: 'replace'; path: string; value: unknown }
  /** Insert `value` into the array at `path`. Appends when `index` is omitted. */
  | { op: 'insert'; path: string; value: unknown; index?: number }
  /** Remove the array element or object property at `path`. */
  | { op: 'delete'; path: string }
  /**
   * JSON-Patch-style precondition: assert the current value at `path` deep-equals
   * `value` (key-order-insensitive). Use to guard against overwriting concurrent
   * human edits. Evaluated in sequence against the evolving candidate.
   */
  | { op: 'test'; path: string; value: unknown };

export type ConfigTransactionResult =
  | {
    ok: true;
    /** Number of mutating ops committed. 0 for a test-only transaction. */
    applied: number;
    dryRun?: true;
    warnings?: string[];
    omittedWarningCount?: number;
  }
  | {
    ok: false;
    errors: string[];
    omittedErrorCount?: number;
    /** Set when a specific op failed structurally (parse, addressing, test precondition). */
    failedOpIndex?: number;
    warnings?: string[];
    omittedWarningCount?: number;
  }
  | {
    /**
     * Catastrophic: the replay onto the live document did not reproduce the
     * validated candidate (an engine bug), and there is no rollback — the
     * document HAS changed. Callers must refresh their view, stop further
     * programmatic edits, and tell the user to reload/discard the branch.
     */
    ok: false;
    committed: true;
    diverged: true;
    errors: string[];
  };

export type TransactionOptions = {
  /** Validate and report without committing. */
  dryRun?: boolean;
  /**
   * Domain-validation seam (the engine cannot import domain packages). Receives
   * both states so validators can implement non-regression without re-obtaining
   * the old document. Errors block; warnings pass through.
   */
  validateCandidate?: (ctx: { before: unknown; candidate: unknown }) => { errors: string[]; warnings: string[] };
  /** Max ops per transaction. Default 100. */
  maxOps?: number;
  /** Max nodes any single traversal may visit before failing closed. Default set from benchmarks. */
  traversalBudget?: number;
  /**
   * Provenance marker for machine-copied values: the transaction's single write
   * op carries a value copied VERBATIM from an existing document, never authored
   * by a model. New schema violations anchored within that written subtree are
   * reported as WARNINGS instead of rejections — stray legacy fields, legacy
   * format misses, and the like ride along exactly as they do in a human
   * copy/paste. Still rejected: wrong placement (an unmatched-union/type
   * violation at the subtree root — where the copy lands is the caller's
   * decision, not source reality) and all id/reference invariants. Restricted
   * to transactions with exactly one write op (plus any number of test ops):
   * the toleration is scoped by the written path, and multiple writes could
   * shift each other's indices.
   */
  verbatimValues?: boolean;
};

/**
 * One structural schema violation. `instancePath` is for display only — the
 * multiset identity used for non-regression is (schemaPath, code, params,
 * offendingData) and deliberately contains no array indices, so pre-existing
 * debt that merely shifts position is not mistaken for a new violation.
 */
export type SchemaViolation = {
  /** Where in the document (display only — NEVER part of the identity). */
  instancePath: string;
  /** Stable rule location: nearest named type + property chain, e.g. "Section.title". */
  schemaPath: string;
  code: 'type' | 'required' | 'unknown-prop' | 'format' | 'blank-id' | 'max-items' | 'unmatched-union';
  /** Rule parameters (expected kind, regex, limit, ...). Part of the identity. */
  params: unknown;
  /** ONLY the data relevant to this rule (see collectViolations). Part of the identity, deep-compared. */
  offendingData: unknown;
  message: string;
};

export const TOO_LARGE_TO_VERIFY =
  'This document or value is too large to check exhaustively — refusing to write rather than '
  + 'writing part of it unverified. Make a smaller, more targeted edit.';

/**
 * Default per-traversal node budget (applies to the schema walk, id scans, and
 * reference scans alike). Benchmarks (2026-08-06, M-series, bun): full-document
 * validation with kind-discriminant union narrowing costs 6.5ms on a synthetic
 * 2.2MB/12k-component doc and 23.8ms on a real-schema 0.7MB/6.6k-component doc;
 * a clean transaction end-to-end is 16ms / 30ms respectively (the before-walk is
 * skipped whenever the candidate is violation-free). Those docs visit well under
 * 200k nodes, so 2M bounds runaway input at roughly half a second before failing
 * closed while leaving ~10x headroom over the largest real configs.
 */
const DEFAULT_TRAVERSAL_BUDGET = 2_000_000;
const DEFAULT_MAX_OPS = 100;
/** Errors/warnings included verbatim in a result; the rest are counted, never silently dropped. */
const DIAGNOSTIC_LIMIT = 20;

// ---------------------------------------------------------------------------
// Path grammar (unchanged from the per-op design).
// ---------------------------------------------------------------------------

/**
 * Property names that must never be traversed or written. `value[key]` on a plain
 * object resolves these to `Object.prototype` and friends, which then either
 * crashes the walk or mutates a prototype instead of the document.
 */
const UNSAFE_PROPS = new Set(['__proto__', 'constructor', 'prototype']);

export type Segment = { kind: 'prop'; prop: string } | { kind: 'index'; index: number };

/**
 * "survey[0].components[1]" -> [prop survey, index 0, prop components, index 1].
 * Returns null on anything that isn't plain property/index access.
 */
export function parseConfigPath(path: string): Segment[] | null {
  const segments: Segment[] = [];
  for (const raw of path.split('.')) {
    if (raw === '') continue;
    const match = raw.match(/^([^[\]]*)((?:\[\d+\])*)$/);
    if (!match) return null;
    const [, prop, brackets] = match;
    if (prop) {
      if (UNSAFE_PROPS.has(prop)) return null;
      segments.push({ kind: 'prop', prop });
    }
    for (const bracket of brackets.matchAll(/\[(\d+)\]/g)) {
      segments.push({ kind: 'index', index: Number(bracket[1]) });
    }
  }
  return segments;
}

function describeSegment(segment: Segment): string {
  return segment.kind === 'prop' ? `.${segment.prop}` : `[${segment.index}]`;
}

function describePath(segments: Segment[]): string {
  return segments.map(describeSegment).join('').replace(/^\./, '') || '(root)';
}

// ---------------------------------------------------------------------------
// Key-order-insensitive deep equality. Property replace on a TrackedRecord is
// remove-then-set, which moves the key to the end — stringify equality would
// false-alarm on every replace. Arrays stay order-sensitive.
// ---------------------------------------------------------------------------

/** Returns the path of the first difference, or null when deep-equal. */
export function diffPath(a: unknown, b: unknown, path = ''): string | null {
  if (a === b) return null;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return path || '(root)';
  }
  const aIsArray = Array.isArray(a);
  if (aIsArray !== Array.isArray(b)) return path || '(root)';
  if (aIsArray) {
    const arrA = a as unknown[];
    const arrB = b as unknown[];
    if (arrA.length !== arrB.length) return `${path}.length`;
    for (let i = 0; i < arrA.length; i++) {
      const diff = diffPath(arrA[i], arrB[i], `${path}[${i}]`);
      if (diff) return diff;
    }
    return null;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return path || '(root)';
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return `${path}.${key}`;
    const diff = diffPath((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key], `${path}.${key}`);
    if (diff) return diff;
  }
  return null;
}

/** Canonical stringify with sorted keys — used only for bucketing identities. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`).join(',')}}`;
}

// ---------------------------------------------------------------------------
// Schema validator: the ONE walker that decides structural validity, run over
// the whole before-document and the whole candidate. Violations are compared
// as a multiset and only NEW ones block, so pre-existing off-schema regions
// never brick editing elsewhere.
// ---------------------------------------------------------------------------

type PropMetaEntry = NonNullable<Node['propMeta']>[string];

// -- Union narrowing ---------------------------------------------------------
// Distro's big unions (Block has ~70 options) are discriminated by the `kind`
// literal. Trying checkRawMatches against every option costs O(options ×
// subtree) per component and dominated whole-document validation in benchmarks;
// narrowing by `kind` first makes it O(subtree). Cached per union node — schema
// nodes are stable for the lifetime of a parse.

const unionKindIndex = new WeakMap<Node, Map<string, Node[]>>();

function optionKindLiteral(option: Node, schema: Schema, depth = 0): string | null {
  const resolved = deref(option, schema);
  if (!resolved || depth > 10) return null;
  if (resolved.kind === 'object') {
    const kindNode = resolved.props.kind ? deref(resolved.props.kind, schema) : undefined;
    return kindNode?.kind === 'literal' && typeof kindNode.value === 'string' ? kindNode.value : null;
  }
  if (resolved.kind === 'intersection') {
    for (const constraint of resolved.constraints) {
      const literal = optionKindLiteral(constraint, schema, depth + 1);
      if (literal) return literal;
    }
  }
  if (resolved.kind === 'union') {
    // A union whose branches all share one kind literal is a WithLegacy pair
    // (modern/legacy shapes of the SAME kind, e.g. CollectionWithId |
    // CollectionWithoutId), and the parser wraps these in the mixin
    // intersections of bigger unions: `(union) & Commentable`. Without seeing
    // through them, `kind: 'Collection'` finds no bucket, falls to the
    // kindless full-match path, and any DEEP failure inside the value
    // collapses into one opaque "matches no branch" at the top — instead of a
    // precise field-level violation. Mixed-kind unions stay unindexable.
    let shared: string | null = null;
    for (const inner of resolved.options) {
      const literal = optionKindLiteral(inner, schema, depth + 1);
      if (literal === null || (shared !== null && literal !== shared)) return null;
      shared = literal;
    }
    return shared;
  }
  return null;
}

function kindIndexFor(union: Node & { kind: 'union' }, schema: Schema): Map<string, Node[]> {
  let index = unionKindIndex.get(union);
  if (!index) {
    index = new Map();
    // Nested unions are FLATTENED into the index: `Collection` inside
    // CollectionComponent is itself a WithLegacy<WithId, WithoutId> union, and
    // without flattening it lands in the kindless bucket — where a deep failure
    // anywhere inside the value collapses into one opaque top-level
    // "matches no branch" instead of a precise field-level violation.
    const add = (option: Node, depth: number) => {
      const resolved = deref(option, schema);
      if (resolved?.kind === 'union' && depth < 10) {
        for (const inner of resolved.options) add(inner, depth + 1);
        return;
      }
      const literal = optionKindLiteral(option, schema) ?? '';
      const bucket = index!.get(literal) ?? [];
      bucket.push(option);
      index!.set(literal, bucket);
    };
    for (const option of union.options) add(option, 0);
    unionKindIndex.set(union, index);
  }
  return index;
}

/**
 * Picks the union branch for a value. When the value's `kind` selects exactly
 * one branch, we descend into it WITHOUT a full-subtree match — that is both
 * the fast path and the better diagnostic: a Section missing `title` reports
 * "missing required property: title" instead of an opaque "matches no branch".
 * Multiple same-kind branches (WithLegacy pairs) are disambiguated by full
 * match, falling back to the first kind-matching branch for granular errors.
 */
function narrowUnion(union: Node & { kind: 'union' }, schema: Schema, value: unknown): Node | 'unmatched' {
  const kindOf = value !== null && typeof value === 'object' && !Array.isArray(value)
    && typeof (value as Record<string, unknown>).kind === 'string'
    ? (value as Record<string, unknown>).kind as string
    : null;
  // Narrowing matches with strictObjects: checkRawMatches is deliberately lenient
  // by default (legacy publish/paste tolerance — a primitive can pass an object
  // type with no required props), but a false match HERE picks the wrong union
  // branch and produces nonsense diagnostics, so narrowing opts into strictness.
  if (kindOf !== null) {
    const index = kindIndexFor(union, schema);
    const byKind = index.get(kindOf) ?? [];
    const kindless = index.get('') ?? [];
    if (byKind.length === 1 && kindless.length === 0) return byKind[0];
    for (const option of byKind) if (checkRawMatches(option, schema, value, undefined, true)) return option;
    for (const option of kindless) if (checkRawMatches(option, schema, value, undefined, true)) return option;
    if (byKind.length > 0) return byKind[0]; // granular errors beat "matches no branch"
    return 'unmatched';
  }
  for (const option of union.options) {
    if (checkRawMatches(option, schema, value, undefined, true)) return option;
  }
  // No full match on a kindless union. If exactly one option matches the value's
  // basic JS shape, descend into it for granular diagnostics — without this,
  // `Root = Survey | ExpandedSurvey` (array vs object) collapses ANY deep
  // invalidity into one opaque root-level violation whose offendingData is the
  // entire document, destroying both the error message and the non-regression
  // granularity that lets pre-existing debt coexist with new edits.
  const shapeMatches = union.options.filter(option => {
    const resolved = deref(option, schema);
    if (!resolved) return false;
    switch (resolved.kind) {
      case 'array': return Array.isArray(value);
      case 'object':
      case 'intersection': return value !== null && typeof value === 'object' && !Array.isArray(value);
      case 'string': return typeof value === 'string';
      case 'number': return typeof value === 'number';
      case 'boolean': return typeof value === 'boolean';
      case 'literal': return typeof value === typeof resolved.value;
      default: return false;
    }
  });
  if (shapeMatches.length === 1) return shapeMatches[0];
  return 'unmatched';
}

type MemberResolution = {
  props: Record<string, Node>;
  required: string[];
  propMeta: Record<string, PropMetaEntry>;
  /** Name of a union constraint no branch of which matched the value, if any. */
  unmatchedUnion: string | null;
};

/**
 * The properties a value has at an object/intersection/union position, with union
 * constraints narrowed against the value. Distro's top level is
 * `((Section & CommonProperties) | Collection | …) & Commentable`, so unions
 * inside intersections are the common case, and a union no branch of which
 * matches must be REPORTED (as unmatched-union), not silently skipped — silence
 * here is how new off-schema regions would go unvalidated.
 */
function resolveMembers(node: Node, schema: Schema, value: unknown): MemberResolution {
  const out: MemberResolution = { props: {}, required: [], propMeta: {}, unmatchedUnion: null };
  const visit = (candidate: Node | undefined, depth: number): void => {
    const resolved = deref(candidate, schema);
    if (!resolved || depth > 50) return;
    if (resolved.kind === 'object') {
      Object.assign(out.props, resolved.props);
      out.required.push(...resolved.required);
      if (resolved.propMeta) Object.assign(out.propMeta, resolved.propMeta);
      return;
    }
    if (resolved.kind === 'intersection') {
      for (const constraint of resolved.constraints) visit(constraint, depth + 1);
      return;
    }
    if (resolved.kind === 'union') {
      const match = narrowUnion(resolved, schema, value);
      if (match === 'unmatched') {
        out.unmatchedUnion = resolved.name ?? (candidate?.kind === 'ref' ? candidate.label : 'union');
        return;
      }
      visit(match, depth + 1);
    }
  };
  visit(node, 0);
  return out;
}

/** Named type a node resolves through, for stable schemaPath bases. */
function refLabel(node: Node | undefined): string | null {
  return node?.kind === 'ref' ? node.label : null;
}

type Traversal<T> = { ok: true; found: T } | { ok: false; exhausted: true };

/**
 * Every structural violation in `value` against `node`.
 *
 * Covers what the editor enforces across scattered UI paths, in one place:
 * type mismatches, required properties, undeclared properties, `@format`
 * regexes, `@uuid` blank identifiers, `maxItems` (read from BOTH node.meta and
 * the containing propMeta — the dual location that bit `@format` before), and
 * unions no branch of which matches (including inside intersections).
 *
 * `offendingData` is scoped to what the rule actually judges, so an unrelated
 * edit to an already-invalid container doesn't read as a new violation:
 * unmatched-union → the offending object; unknown-prop → prop name + value;
 * format → the invalid scalar; max-items → length + limit; required → the
 * MISSING prop names only, never the whole container.
 */
export function collectViolations(
  node: Node | undefined,
  schema: Schema,
  value: unknown,
  budget = DEFAULT_TRAVERSAL_BUDGET,
): Traversal<SchemaViolation[]> {
  const found: SchemaViolation[] = [];
  type Frame = {
    node: Node | undefined;
    value: unknown;
    instancePath: string;
    schemaPath: string;
    meta?: PropMetaEntry;
  };
  const stack: Frame[] = [{ node, value, instancePath: '', schemaPath: refLabel(node) ?? 'Root' }];
  let visited = 0;

  const violation = (frame: Frame, code: SchemaViolation['code'], params: unknown, offendingData: unknown, message: string) => {
    found.push({
      instancePath: frame.instancePath || '(root)',
      schemaPath: frame.schemaPath,
      code,
      params,
      offendingData,
      message: `${frame.instancePath || '(root)'}: ${message}`,
    });
  };

  while (stack.length > 0) {
    if (++visited > budget) return { ok: false, exhausted: true };
    const frame = stack.pop()!;
    const v = frame.value;
    if (v === undefined) continue; // absence is judged by the parent's required check
    let n = frame.node;
    // Follow refs, resetting the schemaPath base to the named type.
    for (let hops = 0; n?.kind === 'ref' && hops < 100; hops++) {
      frame.schemaPath = n.label;
      n = schema[n.label];
    }
    if (!n) continue;
    if (v === null) {
      // null is a VALUE, not absence: a committed null crashes TrackedRecord.read()
      // ('_trackedObjMeta' in null). Only an explicit null literal accepts it.
      if (!(n.kind === 'literal' && n.value === null)
        && !(n.kind === 'union' && n.options.some(option => { const r = deref(option, schema); return r?.kind === 'literal' && r.value === null; }))) {
        violation(frame, 'type', n.kind === 'literal' ? n.value : n.kind, null, 'is null, which this field does not allow');
      }
      continue;
    }

    switch (n.kind) {
      case 'string': {
        if (typeof v !== 'string') {
          violation(frame, 'type', 'string', v, `expected a string, got ${Array.isArray(v) ? 'an array' : typeof v}`);
          break;
        }
        const { format, formatMessage, isIdentifier } = stringConstraints(n, frame.meta);
        if (isIdentifier && v.trim() === '') {
          violation(frame, 'blank-id', null, null, 'is an identifier and cannot be blank');
        } else if (format) {
          let matches = true;
          try {
            matches = new RegExp(format).test(v);
          } catch { /* an unparseable annotation is not the value's fault */ }
          if (!matches) {
            violation(frame, 'format', format, v,
              `("${v.length > 40 ? `${v.slice(0, 40)}…` : v}") must match ${format}${formatMessage ? ` — ${formatMessage}` : ''}`);
          }
        }
        break;
      }
      case 'number':
        if (typeof v !== 'number') violation(frame, 'type', 'number', v, `expected a number, got ${typeof v}`);
        else if (!Number.isFinite(v)) violation(frame, 'type', 'number', String(v), `is ${v} — only finite numbers are allowed (it would serialize to null)`);
        break;
      case 'boolean':
        if (typeof v !== 'boolean') violation(frame, 'type', 'boolean', v, `expected a boolean, got ${typeof v}`);
        break;
      case 'literal':
        if (v !== n.value) violation(frame, 'type', n.value, v, `expected ${JSON.stringify(n.value)}`);
        break;
      case 'array': {
        if (!Array.isArray(v)) {
          violation(frame, 'type', 'array', v, `expected an array, got ${typeof v}`);
          break;
        }
        const maxItems = n.meta?.maxItems ?? frame.meta?.maxItems;
        if (maxItems !== undefined && v.length > maxItems) {
          violation(frame, 'max-items', maxItems, { length: v.length, maxItems }, `has ${v.length} items; at most ${maxItems} allowed`);
        }
        for (let i = v.length - 1; i >= 0; i--) {
          stack.push({
            node: n.element,
            value: v[i],
            instancePath: `${frame.instancePath}[${i}]`,
            schemaPath: `${frame.schemaPath}[]`,
            meta: frame.meta,
          });
        }
        break;
      }
      case 'union': {
        const match = narrowUnion(n, schema, v);
        if (match === 'unmatched') {
          violation(frame, 'unmatched-union', frame.schemaPath, v, 'matches no branch of its declared type');
          break;
        }
        stack.push({ ...frame, node: match });
        break;
      }
      case 'object':
      case 'intersection': {
        if (typeof v !== 'object' || Array.isArray(v)) {
          violation(frame, 'type', 'object', v, `expected an object, got ${Array.isArray(v) ? 'an array' : typeof v}`);
          break;
        }
        const membersHere = resolveMembers(n, schema, v);
        if (membersHere.unmatchedUnion) {
          // The value's own shape is unknown — report once and don't cascade
          // unknown-prop noise from a schema we couldn't resolve.
          violation(frame, 'unmatched-union', membersHere.unmatchedUnion, v, 'matches no branch of its declared type');
          break;
        }
        const record = v as Record<string, unknown>;
        // One violation PER missing prop, not one carrying the whole list: with a
        // list identity, supplying SOME of a pre-existing debt's missing props
        // changes the identity and the improvement reads as a NEW violation —
        // partially fixing debt would be rejected. Per-prop identities shrink
        // monotonically as debt is paid down.
        for (const prop of new Set(membersHere.required)) {
          if (record[prop] === undefined) {
            violation(frame, 'required', prop, prop, `is missing required property: ${prop}`);
          }
        }
        for (const [key, child] of Object.entries(record)) {
          if (!membersHere.props[key]) {
            violation({ ...frame, instancePath: `${frame.instancePath}.${key}` }, 'unknown-prop', key, { prop: key, value: child },
              'is not declared by the Distro schema — remove it, Distro rejects unknown fields');
            continue;
          }
          stack.push({
            node: membersHere.props[key],
            value: child,
            instancePath: `${frame.instancePath}.${key}`,
            schemaPath: `${frame.schemaPath}.${key}`,
            meta: membersHere.propMeta[key],
          });
        }
        break;
      }
      default:
        break; // typeParameter etc.: nothing checkable
    }
  }
  return { ok: true, found };
}

/**
 * Violations in `after` that were not present in `before`, as a multiset.
 * Bucketing is by (schemaPath, code, params); matches within a bucket are
 * CONFIRMED with key-order-insensitive deep equality of `offendingData` — a
 * fingerprint alone must never decide whether a regression is accepted.
 */
export function diffViolations(before: SchemaViolation[], after: SchemaViolation[]): SchemaViolation[] {
  const buckets = new Map<string, unknown[]>();
  const keyOf = (violation: SchemaViolation) =>
    `${violation.schemaPath}|${violation.code}|${stableStringify(violation.params)}`;
  for (const violation of before) {
    const key = keyOf(violation);
    const bucket = buckets.get(key) ?? [];
    bucket.push(violation.offendingData);
    buckets.set(key, bucket);
  }
  return after.filter(violation => {
    const bucket = buckets.get(keyOf(violation));
    if (!bucket) return true;
    const matchIndex = bucket.findIndex(data => diffPath(data, violation.offendingData) === null);
    if (matchIndex === -1) return true;
    bucket.splice(matchIndex, 1); // consume: multiplicity still counts duplicates
    return false;
  });
}

// ---------------------------------------------------------------------------
// Id and reference integrity, checked as regressions on the plain documents.
// ---------------------------------------------------------------------------

/** Every string under an `id` key, in document order. Duplicates preserved. */
export function idsInValue(value: unknown, budget = DEFAULT_TRAVERSAL_BUDGET): Traversal<string[]> {
  const into: string[] = [];
  const stack: unknown[] = [value];
  let visited = 0;
  while (stack.length > 0) {
    if (++visited > budget) return { ok: false, exhausted: true };
    const v = stack.pop();
    if (v === null || typeof v !== 'object') continue;
    if (Array.isArray(v)) {
      for (const item of v) stack.push(item);
      continue;
    }
    for (const [key, child] of Object.entries(v)) {
      if (key === 'id' && typeof child === 'string') into.push(child);
      else stack.push(child);
    }
  }
  return { ok: true, found: into };
}

type DocumentRefs = {
  /** Definition id → component kind. First occurrence wins, matching the editor's definitionIndex. */
  definitions: Map<string, string | undefined>;
  /** Definition references: `X Reference` blocks carrying a definitionId. */
  references: { ownId?: string; definitionId: string; expectedKind: string }[];
  navTargets: string[];
};

/**
 * The two reference conventions the document can express, mirroring the real
 * resolver (packages/types/src/translation/references.ts): new-format
 * definitions (`kind: 'Definition'` → component.kind / id) and legacy ones
 * (component kind at top level, id under definitionProps).
 */
function collectRefs(doc: unknown, budget: number): Traversal<DocumentRefs> {
  const out: DocumentRefs = { definitions: new Map(), references: [], navTargets: [] };
  const stack: unknown[] = [doc];
  let visited = 0;
  while (stack.length > 0) {
    if (++visited > budget) return { ok: false, exhausted: true };
    const v = stack.pop();
    if (v === null || typeof v !== 'object') continue;
    if (Array.isArray(v)) {
      // Reverse push to keep document order (first-wins semantics for definitions).
      for (let i = v.length - 1; i >= 0; i--) stack.push(v[i]);
      continue;
    }
    const record = v as Record<string, unknown>;
    const kind = typeof record.kind === 'string' ? record.kind : undefined;

    if (kind === 'Definition' && typeof record.id === 'string') {
      if (!out.definitions.has(record.id)) {
        const component = record.component as Record<string, unknown> | undefined;
        out.definitions.set(record.id, typeof component?.kind === 'string' ? component.kind : undefined);
      }
    } else {
      const definitionProps = record.definitionProps as Record<string, unknown> | undefined;
      if (definitionProps?.kind === 'Definition' && typeof definitionProps.id === 'string' && !out.definitions.has(definitionProps.id)) {
        out.definitions.set(definitionProps.id, kind);
      }
    }

    if (kind?.endsWith(' Reference') && typeof record.definitionId === 'string') {
      out.references.push({
        ownId: typeof record.id === 'string' ? record.id : undefined,
        definitionId: record.definitionId,
        expectedKind: kind.slice(0, -' Reference'.length),
      });
    }
    if (kind === 'Navigation Reference Node' && typeof record.target === 'string') {
      out.navTargets.push(record.target);
    }

    // Push in an order that yields document-order pops for first-wins definitions.
    const entries = Object.entries(record);
    for (let i = entries.length - 1; i >= 0; i--) stack.push(entries[i][1]);
  }
  return { ok: true, found: out };
}

function referenceResolves(ref: DocumentRefs['references'][number], refs: DocumentRefs): boolean {
  // Mirrors the real resolver (translation/references.ts): kind must match
  // exactly. A malformed definition with no derivable component kind resolves
  // NOTHING — treating it as compatible-with-everything silently blessed new
  // references to pre-existing broken definitions.
  return refs.definitions.get(ref.definitionId) === ref.expectedKind;
}

/**
 * Heuristic for navigation targets that never resolve by component id BY DESIGN
 * (permissions.ts resolves /p/<path>, /d/<path>, hrefs, and name slugs too).
 * These are invisible to the id-membership check and must not generate noise.
 */
function looksLikeNonIdNavTarget(target: string): boolean {
  return target.startsWith('/') || target.startsWith('#') || target.startsWith('name:') || target.includes('://');
}

function checkIdAndReferenceInvariants(
  before: unknown,
  candidate: unknown,
  budget: number,
): { errors: string[]; warnings: string[] } | 'exhausted' {
  const errors: string[] = [];
  const warnings: string[] = [];

  const idsBefore = idsInValue(before, budget);
  const idsAfter = idsInValue(candidate, budget);
  if (!idsBefore.ok || !idsAfter.ok) return 'exhausted';

  const countBlank = (ids: string[]) => ids.filter(id => id.trim() === '').length;
  if (countBlank(idsAfter.found) > countBlank(idsBefore.found)) {
    errors.push('An "id" cannot be blank — it is what Navigation entries and References resolve against. '
      + 'Give it a unique, non-empty value.');
  }

  const multiset = (ids: string[]) => {
    const counts = new Map<string, number>();
    for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
    return counts;
  };
  const beforeCounts = multiset(idsBefore.found);
  const afterCounts = multiset(idsAfter.found);
  for (const [id, count] of afterCounts) {
    if (count > 1 && count > (beforeCounts.get(id) ?? 0)) {
      errors.push(`The id "${id}" would appear ${count} times — an id already present elsewhere in this config `
        + 'cannot be reused. Ids must be unique: a duplicate makes References resolve to the wrong component.');
    }
  }

  const refsBefore = collectRefs(before, budget);
  const refsAfter = collectRefs(candidate, budget);
  if (!refsBefore.ok || !refsAfter.ok) return 'exhausted';

  // Definition references, correlated by the reference's OWN id so retargeting
  // an existing reference to a missing definition is a regression (block), while
  // a brand-new unresolved reference only warns.
  const resolvedBefore = new Map<string, boolean>();
  for (const ref of refsBefore.found.references) {
    if (ref.ownId !== undefined && !resolvedBefore.has(ref.ownId)) {
      resolvedBefore.set(ref.ownId, referenceResolves(ref, refsBefore.found));
    }
  }
  for (const ref of refsAfter.found.references) {
    const resolvesNow = referenceResolves(ref, refsAfter.found);
    if (resolvesNow) continue;
    const wasResolved = ref.ownId !== undefined ? resolvedBefore.get(ref.ownId) : undefined;
    if (wasResolved === true) {
      const why = refsAfter.found.definitions.has(ref.definitionId)
        ? 'points at a definition of a different kind'
        : 'matches no definition';
      errors.push(`The ${ref.expectedKind} Reference "${ref.ownId}" resolved before this transaction but would not after it `
        + `(definitionId "${ref.definitionId}" ${why}). `
        + 'Update the reference and its definition together in one transaction, or leave both alone.');
    } else if (wasResolved === undefined) {
      warnings.push(`New ${ref.expectedKind} Reference${ref.ownId ? ` "${ref.ownId}"` : ''} does not resolve: no definition with id `
        + `"${ref.definitionId}"${refsAfter.found.definitions.has(ref.definitionId) ? ' of that kind' : ''} exists yet. `
        + 'Add the definition, or fix the definitionId.');
    }
    // wasResolved === false: pre-existing dangling reference — tolerated debt.
  }

  // Navigation targets have no own id to correlate by, so the rule keys on
  // targets present in the CANDIDATE: one that used to resolve by id must keep
  // resolving; removed/retargeted entries impose nothing from their old target.
  const idSetBefore = new Set(idsBefore.found);
  const idSetAfter = new Set(idsAfter.found);
  const navTargetsBefore = new Set(refsBefore.found.navTargets);
  for (const target of refsAfter.found.navTargets) {
    if (idSetBefore.has(target) && !idSetAfter.has(target)) {
      errors.push(`A Navigation entry targets "${target}", which this transaction removes. `
        + 'Remove or retarget the Navigation entry in the same transaction, or keep the id.');
    } else if (!idSetBefore.has(target) && !idSetAfter.has(target)
      && !navTargetsBefore.has(target) && !looksLikeNonIdNavTarget(target)) {
      warnings.push(`New Navigation target "${target}" does not match any component id. `
        + 'If it is meant to be an id, add that component; path/name targets can be ignored.');
    }
  }

  return { errors, warnings };
}

// ---------------------------------------------------------------------------
// Structural normalization + candidate application. NO schema here: a
// transaction may pass through invalid intermediate states; only the final
// candidate is judged.
// ---------------------------------------------------------------------------

type ResolvedOp =
  | { kind: 'set-prop'; container: Segment[]; prop: string; value: unknown }
  | { kind: 'replace-elem'; container: Segment[]; index: number; value: unknown }
  | { kind: 'insert'; container: Segment[]; index: number; value: unknown }
  | { kind: 'delete-prop'; container: Segment[]; prop: string }
  | { kind: 'delete-elem'; container: Segment[]; index: number };

/**
 * Rejects anything that isn't plain, JSON-safe data. structuredClone alone is
 * not enough: it happily clones Date/Map/Set (which JSON-serialize to junk or
 * {}), and JSON.parse('1e400') hands us Infinity (which serializes to null).
 * The CRDT and everything downstream assume plain JSON documents.
 */
function invalidPlainJson(value: unknown, path = 'value', depth = 0): string | null {
  if (value === null) return null;
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return null;
    case 'number':
      return Number.isFinite(value) ? null : `${path} is ${value} — only finite numbers can be written`;
    case 'object':
      break;
    default:
      return `${path} is a ${typeof value} — only plain JSON data (objects, arrays, strings, finite numbers, booleans, null) can be written`;
  }
  if (depth > 200) return `${path} is nested too deeply`;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const bad = invalidPlainJson(value[i], `${path}[${i}]`, depth + 1);
      if (bad) return bad;
    }
    return null;
  }
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    return `${path} is a ${value?.constructor?.name ?? 'non-plain'} object — only plain JSON objects can be written`;
  }
  for (const [key, child] of Object.entries(value)) {
    const bad = invalidPlainJson(child, `${path}.${key}`, depth + 1);
    if (bad) return bad;
  }
  return null;
}

type PlainWalk = { ok: true; value: unknown } | { ok: false; error: string };

function plainValueAt(doc: unknown, segments: Segment[]): PlainWalk {
  let current: unknown = doc;
  let walked = '';
  for (const segment of segments) {
    if (segment.kind === 'prop') {
      if (current === null || typeof current !== 'object' || Array.isArray(current)) {
        return { ok: false, error: `Path "${walked || '(root)'}" is ${Array.isArray(current) ? 'an array' : 'not an object'} — cannot read property "${segment.prop}".` };
      }
      const record = current as Record<string, unknown>;
      if (!Object.prototype.hasOwnProperty.call(record, segment.prop)) {
        return { ok: false, error: `Property "${segment.prop}" does not exist at "${walked || '(root)'}". Available: ${Object.keys(record).join(', ')}` };
      }
      current = record[segment.prop];
    } else {
      if (!Array.isArray(current)) {
        return { ok: false, error: `Path "${walked || '(root)'}" is not an array — cannot index [${segment.index}].` };
      }
      if (segment.index < 0 || segment.index >= current.length) {
        return { ok: false, error: `Index ${segment.index} out of range at "${walked || '(root)'}" (length ${current.length}).` };
      }
      current = current[segment.index];
    }
    walked += describeSegment(segment);
  }
  return { ok: true, value: current };
}

/**
 * Normalizes one user op against the evolving candidate and applies it.
 *
 * CRITICAL — replay-value isolation: the value stored in the ResolvedOp and the
 * value spliced into the candidate are SEPARATE clones. If they were the same
 * object, a later op mutating inside a just-inserted subtree (insert {items:[]},
 * then insert X into items) would mutate the replay snapshot too, and replay
 * would apply X twice — a guaranteed post-commit divergence.
 */
function normalizeAndApplyOp(
  candidate: unknown,
  op: ConfigEditOp,
  opIndex: number,
): { resolved?: ResolvedOp } | { error: string } {
  const fail = (message: string) => ({ error: `edit ${opIndex}: ${message}` });

  const segments = parseConfigPath(op.path);
  if (!segments) {
    return fail(`Could not parse path "${op.path}". Use property and index access only, e.g. "survey[0].components[1]".`);
  }

  if (op.op === 'test') {
    const at = plainValueAt(candidate, segments);
    if (!at.ok) return fail(at.error);
    const difference = diffPath(at.value, op.value);
    if (difference !== null) {
      return fail(`test precondition failed at "${op.path}" (first difference at ${difference}). `
        + 'The document does not match what you expected — re-read it with getObject before editing.');
    }
    return {};
  }

  let replayValue: unknown;
  let candidateValue: unknown;
  if (op.op !== 'delete') {
    const notJson = invalidPlainJson(op.value);
    if (notJson) return fail(`${notJson}.`);
    try {
      replayValue = structuredClone(op.value);
      candidateValue = structuredClone(replayValue);
    } catch {
      return fail('the value could not be cloned — only plain JSON data (objects, arrays, strings, finite numbers, booleans, null) can be written.');
    }
  }

  if (op.op === 'insert') {
    const at = plainValueAt(candidate, segments);
    if (!at.ok) return fail(at.error);
    if (!Array.isArray(at.value)) {
      return fail(`Path "${op.path}" is not an array. Insert into the array itself, e.g. "survey[0].components".`);
    }
    if (op.index !== undefined && (!Number.isInteger(op.index) || op.index < 0)) {
      // A fractional index would splice at floor() locally while the CRDT finds no
      // neighbours at a fractional key and mints an as-if-empty sort key — the
      // document would replay in a different order than it displays.
      return fail(`index must be a whole number of 0 or more, got ${op.index}.`);
    }
    const index = op.index === undefined ? at.value.length : Math.min(op.index, at.value.length);
    at.value.splice(index, 0, candidateValue);
    return { resolved: { kind: 'insert', container: segments, index, value: replayValue } };
  }

  const last = segments[segments.length - 1];
  if (!last) return fail(`Cannot ${op.op} the document root. Target a property or array element.`);
  const containerSegments = segments.slice(0, -1);
  const at = plainValueAt(candidate, containerSegments);
  if (!at.ok) return fail(at.error);
  const container = at.value;

  if (last.kind === 'prop') {
    if (container === null || typeof container !== 'object' || Array.isArray(container)) {
      return fail(`Cannot ${op.op} property "${last.prop}" — its container is ${Array.isArray(container) ? 'an array' : 'not an object'}.`);
    }
    const record = container as Record<string, unknown>;
    const exists = Object.prototype.hasOwnProperty.call(record, last.prop);
    if (op.op === 'delete') {
      if (!exists) return fail(`Property "${last.prop}" does not exist at "${op.path}".`);
      delete record[last.prop];
      return { resolved: { kind: 'delete-prop', container: containerSegments, prop: last.prop } };
    }
    record[last.prop] = candidateValue;
    return { resolved: { kind: 'set-prop', container: containerSegments, prop: last.prop, value: replayValue } };
  }

  if (!Array.isArray(container)) {
    return fail(`Cannot ${op.op} index [${last.index}] — its container is not an array.`);
  }
  if (last.index < 0 || last.index >= container.length) {
    return fail(`Index ${last.index} out of range at "${describePath(containerSegments)}" (length ${container.length}).`);
  }
  if (op.op === 'delete') {
    container.splice(last.index, 1);
    return { resolved: { kind: 'delete-elem', container: containerSegments, index: last.index } };
  }
  container.splice(last.index, 1, candidateValue);
  return { resolved: { kind: 'replace-elem', container: containerSegments, index: last.index, value: replayValue } };
}

// ---------------------------------------------------------------------------
// Replay: schema-free structural mutations onto the live tracked tree, inside
// ONE changeset. By induction the tracked tree before op k is structurally
// identical to the candidate before op k, so addressing cannot differ; any
// failure here is an engine bug and is surfaced as the catastrophic result.
// ---------------------------------------------------------------------------

function trackedKind(obj: TrackedObject): 'record' | 'array' | 'primitive' {
  return obj.state.objects[obj.id]?.kind as 'record' | 'array' | 'primitive';
}

function locateTracked(root: TrackedObject, segments: Segment[]): TrackedObject {
  let obj = root;
  for (const segment of segments) {
    if (segment.kind === 'prop') {
      const child = Object.prototype.hasOwnProperty.call((obj as TrackedRecord).value, segment.prop)
        ? (obj as TrackedRecord).value[segment.prop] as TrackedObject
        : undefined;
      if (!child) throw new Error(`replay: property "${segment.prop}" missing`);
      obj = child;
    } else {
      const child = (obj as TrackedArray).value[segment.index];
      if (!child) throw new Error(`replay: index ${segment.index} missing`);
      obj = child;
    }
  }
  return obj;
}

/**
 * Brackets a mutation so everything it touches commits as one changeset — one
 * entry in undo history, one batch of collab events.
 */
function commit(state: State, mutate: () => void): void {
  openChangeset(state);
  try {
    mutate();
  } finally {
    closeChangeset(state);
  }
}

function replayResolved(root: TrackedObject, resolvedOps: ResolvedOp[]): Set<TrackedArray> {
  const touchedArrays = new Set<TrackedArray>();
  commit(root.state, () => {
    for (const rop of resolvedOps) {
      const container = locateTracked(root, rop.container);
      switch (rop.kind) {
        case 'set-prop': {
          const record = container as TrackedRecord;
          const newElement = createFromRawValue(root.state, rop.value);
          if (record.value[rop.prop]) record.remove(rop.prop);
          record.set(rop.prop, newElement);
          newElement.locateUserHere();
          break;
        }
        case 'replace-elem': {
          const array = container as TrackedArray;
          touchedArrays.add(array);
          // No remove-then-insert-at-same-index shortcut exists: insert() derives
          // its sort key from the neighbours, so removing first puts the
          // replacement back in place.
          const newElement = createFromRawValue(root.state, rop.value);
          array.remove(rop.index);
          array.insert(rop.index, newElement);
          newElement.locateUserHere();
          break;
        }
        case 'insert': {
          const array = container as TrackedArray;
          touchedArrays.add(array);
          const newElement = createFromRawValue(root.state, rop.value);
          array.insert(rop.index, newElement);
          newElement.locateUserHere();
          break;
        }
        case 'delete-prop':
          (container as TrackedRecord).remove(rop.prop);
          break;
        case 'delete-elem': {
          touchedArrays.add(container as TrackedArray);
          (container as TrackedArray).remove(rop.index);
          break;
        }
      }
    }
  });
  return touchedArrays;
}

/**
 * In-session array order comes from splice, so the deep-equal cannot see a
 * sort-key ordering bug — it would only appear after event replay/reload.
 * This cheap post-commit check catches it immediately.
 */
function sortKeysMonotonic(touched: Set<TrackedArray>): boolean {
  for (const array of touched) {
    for (let i = 1; i < array.value.length; i++) {
      const previous = array.value[i - 1].sortKey;
      const current = array.value[i].sortKey;
      if (previous && current && SortFunction(previous, current) > 0) return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// The transaction.
// ---------------------------------------------------------------------------

function bounded(list: string[]): { shown: string[]; omitted: number } {
  return list.length <= DIAGNOSTIC_LIMIT
    ? { shown: list, omitted: 0 }
    : { shown: list.slice(0, DIAGNOSTIC_LIMIT), omitted: list.length - DIAGNOSTIC_LIMIT };
}

function rejection(errors: string[], warnings: string[] = [], failedOpIndex?: number): ConfigTransactionResult {
  const boundedErrors = bounded(errors);
  const boundedWarnings = bounded(warnings);
  return {
    ok: false,
    errors: boundedErrors.shown,
    ...(boundedErrors.omitted > 0 && { omittedErrorCount: boundedErrors.omitted }),
    ...(failedOpIndex !== undefined && { failedOpIndex }),
    ...(boundedWarnings.shown.length > 0 && { warnings: boundedWarnings.shown }),
    ...(boundedWarnings.omitted > 0 && { omittedWarningCount: boundedWarnings.omitted }),
  };
}

/** Appended to a violation message when the model probably array-wrapped a bare component out of habit. */
function singletonHint(violation: SchemaViolation): string {
  return violation.code === 'type' && Array.isArray(violation.offendingData) && violation.offendingData.length === 1
    ? ' (did you wrap a single component in an array? Pass the bare object.)'
    : '';
}

/**
 * The engine NEVER throws across this boundary: pre-commit failures of any kind
 * (including engine bugs) return an ordinary rejection, and anything that goes
 * wrong once the live replay has begun returns the catastrophic
 * committed+diverged shape — headless callers (server/MCP) have no DOM bridge
 * to catch for them, and an escaped exception after mutation would violate the
 * all-or-nothing contract silently.
 */
export function applyConfigTransaction(
  root: TrackedObject,
  rootNode: Node,
  schema: Schema,
  ops: ConfigEditOp[],
  opts: TransactionOptions = {},
): ConfigTransactionResult {
  const committed = { value: false };
  try {
    return runTransaction(root, rootNode, schema, ops, opts, committed);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (committed.value) {
      return {
        ok: false,
        committed: true,
        diverged: true,
        errors: [`The transaction failed after changes were applied (${message}). `
          + 'The document may be partially changed and no longer matches what was validated. '
          + 'Do not make further edits — re-read the config with getObject and ask the user to reload or discard this branch.'],
      };
    }
    return rejection([`The transaction failed unexpectedly before anything was written (${message}). Nothing was changed.`]);
  }
}

function runTransaction(
  root: TrackedObject,
  rootNode: Node,
  schema: Schema,
  ops: ConfigEditOp[],
  opts: TransactionOptions,
  committed: { value: boolean },
): ConfigTransactionResult {
  const maxOps = opts.maxOps ?? DEFAULT_MAX_OPS;
  const budget = opts.traversalBudget ?? DEFAULT_TRAVERSAL_BUDGET;

  if (!Array.isArray(ops) || ops.length === 0) {
    return rejection(['A transaction needs at least one edit.']);
  }
  if (ops.length > maxOps) {
    return rejection([`Too many edits in one transaction (${ops.length}; at most ${maxOps}). Split the change into batches.`]);
  }

  // Two independent materializations: `before` stays pristine for the
  // non-regression comparisons (the cheap, shared, memoized read() — never
  // mutated), `candidate` evolves under the ops so it must be a fresh,
  // exclusively-owned copy (useCache: false) — read() with no args would
  // hand back that SAME cached reference as `before`, and normalizeAndApplyOp
  // below mutates `candidate` in place, which would corrupt `before` too (and
  // throw, because cached read() output is frozen).
  const before = root.read();
  const candidate = root.read(undefined, false);

  const resolvedOps: ResolvedOp[] = [];
  for (let i = 0; i < ops.length; i++) {
    const outcome = normalizeAndApplyOp(candidate, ops[i], i);
    if ('error' in outcome) return rejection([outcome.error], [], i);
    if (outcome.resolved) resolvedOps.push(outcome.resolved);
  }

  // A test-only transaction changes nothing and must not report anything a
  // no-op cannot cause — no validation, no changeset, no generation bump.
  // (Validation-without-writing is what dryRun is for.)
  if (resolvedOps.length === 0) {
    return { ok: true, applied: 0, ...(opts.dryRun && { dryRun: true as const }) };
  }

  // Where a verbatim-copied value landed, as a display-path prefix. Only
  // meaningful for a single write op — the path of one write can be shifted by
  // another, so verbatimValues refuses multi-write transactions outright.
  let verbatimPrefix: string | undefined;
  if (opts.verbatimValues) {
    const writes = resolvedOps.filter(op => op.kind === 'set-prop' || op.kind === 'replace-elem' || op.kind === 'insert');
    if (writes.length !== 1) {
      return rejection([`verbatimValues requires exactly one write op (got ${writes.length}); split the transaction.`]);
    }
    const w = writes[0];
    const container = w.container.map(s => s.kind === 'prop' ? `.${s.prop}` : `[${s.index}]`).join('');
    verbatimPrefix = w.kind === 'set-prop' ? `${container}.${w.prop}` : `${container}[${w.index}]`;
  }

  // --- Final-state validation ---------------------------------------------
  // Candidate first: when it's clean (the common case) nothing can be new and
  // the before-walk is never needed at all.
  const violationsAfter = collectViolations(rootNode, schema, candidate, budget);
  if (!violationsAfter.ok) return rejection([TOO_LARGE_TO_VERIFY]);

  const errors: string[] = [];
  const warnings: string[] = [];
  if (violationsAfter.found.length > 0) {
    const violationsBefore = collectViolations(rootNode, schema, before, budget);
    if (!violationsBefore.ok) return rejection([TOO_LARGE_TO_VERIFY]);
    for (const violation of diffViolations(violationsBefore.found, violationsAfter.found)) {
      // Verbatim-copied content: violations inside the copied subtree are
      // pre-existing source reality — undeclared legacy fields, format-rule
      // misses ("short-flup-stats" paths, choice values with spaces) — that
      // human paste would keep too, so they warn instead of reject. The one
      // thing still rejected is wrong PLACEMENT (the copied value not matching
      // the destination slot at all, anchored at the subtree root): where the
      // copy lands is the model's decision, not source reality.
      if (verbatimPrefix !== undefined) {
        const within = violation.instancePath === verbatimPrefix
          || violation.instancePath.startsWith(`${verbatimPrefix}.`)
          || violation.instancePath.startsWith(`${verbatimPrefix}[`);
        const placementError = violation.instancePath === verbatimPrefix
          && (violation.code === 'unmatched-union' || violation.code === 'type');
        if (within && !placementError) {
          warnings.push(`Copied verbatim despite schema rule: ${violation.message}`);
          continue;
        }
      }
      errors.push(violation.message + singletonHint(violation));
    }
  }

  const invariants = checkIdAndReferenceInvariants(before, candidate, budget);
  if (invariants === 'exhausted') return rejection([TOO_LARGE_TO_VERIFY]);
  errors.push(...invariants.errors);
  warnings.push(...invariants.warnings);

  if (opts.validateCandidate) {
    const domain = opts.validateCandidate({ before, candidate });
    errors.push(...domain.errors);
    warnings.push(...domain.warnings);
  }

  if (errors.length > 0) {
    return rejection(errors, warnings);
  }

  if (opts.dryRun) {
    const boundedWarnings = bounded(warnings);
    return {
      ok: true,
      applied: resolvedOps.length,
      dryRun: true,
      ...(boundedWarnings.shown.length > 0 && { warnings: boundedWarnings.shown }),
      ...(boundedWarnings.omitted > 0 && { omittedWarningCount: boundedWarnings.omitted }),
    };
  }

  // --- Commit ---------------------------------------------------------------
  // From this point on, ANY exception (replay itself, the post-commit read,
  // the equality check) means the document changed in ways we could not verify;
  // the outer boundary turns it into the catastrophic result.
  committed.value = true;
  const touchedArrays = replayResolved(root, resolvedOps);

  const divergence = diffPath(root.read(), candidate);
  if (divergence !== null || !sortKeysMonotonic(touchedArrays)) {
    return {
      ok: false,
      committed: true,
      diverged: true,
      errors: [`The transaction was applied, but the resulting document does not match the validated result`
        + `${divergence !== null ? ` (first difference at ${divergence})` : ' (element ordering diverged)'}. `
        + 'Do not make further edits — re-read the config with getObject and ask the user to reload or discard this branch.'],
    };
  }

  const boundedWarnings = bounded(warnings);
  return {
    ok: true,
    applied: resolvedOps.length,
    ...(boundedWarnings.shown.length > 0 && { warnings: boundedWarnings.shown }),
    ...(boundedWarnings.omitted > 0 && { omittedWarningCount: boundedWarnings.omitted }),
  };
}
